// The guide button (Stage 2, docs/property-strategy-spec.md S2.3 and S2.8; the build plan, section 5). Browser only.
// It opens the starter guide for the exercise the report itself shows (Adam, 28 October 2026, on the starter guides'
// recommendations: "Yes, all of them."), so each of the 14 guides is reached by the readers whose report shows its
// exercise: report-content.mjs reportExercise names the exercise, the same key buildReportContent picks it by, and
// GUIDES gives its guide. Each guide has 1 version for each next step its readers can reach (Adam, 28 Oct 2026: "They
// need to point to the same step."), so the map is keyed by the guide, then by the next step from lead-labels.mjs
// nextStep, the rule email 5 reads too. For example { 'BRRR starter guide': { masterclass: { label: 'Open your BRRR
// starter guide', href: '...' } } }; an entry adds 1 button to the report's exercise step. With no version for the
// lead's own next step there is no button, never another step's version, and an exercise with no guide has none.
// Free resources shows the same guide for a saved report (savedGuide).
import {reportExercise} from './report-content.mjs';
import {leadLabels,nextStep} from './lead-labels.mjs';
import {evaluate} from './engine.mjs';
import {upgradeSession} from './session.mjs';

// Each report exercise and its guide (the guides' README, "REPORT STEP AND ITS GUIDE"): 14 guides. An open single-let
// setup shares buy-to-let's guide, rent-to-rent's 2 exercises (rooms and short stays) share 1, and an open choice of
// development stage shares development's (planning for a plot sale is development's own exercise); refurbishing to let
// has its own. The shortlist, none_appeals, compare_work and goal_open exercises have no guide. The versions each guide
// has (the README's table): property-school, masterclass (its "Video") and scorecard for BRRR, buy-to-let, HMO, serviced
// accommodation, buy-to-let with refurbishment, flips, commercial conversions and development; property-school and
// scorecard for deal sourcing; masterclass and scorecard for rent-to-rent, lease option and assisted sales; none (no
// offer) for the 2 investor guides.
export const GUIDES={
 BRR:'BRRR starter guide',
 BTL:'Buy-to-let starter guide',single_let_setup_open:'Buy-to-let starter guide',
 HMO:'HMO starter guide',
 SA:'Serviced accommodation starter guide',
 rent_to_rent_HMO:'Rent-to-rent starter guide',rent_to_rent_SA:'Rent-to-rent starter guide',
 lease_option:'Lease option starter guide',
 FLIP:'Flips starter guide',
 refurbish_to_let:'Buy-to-let with refurbishment starter guide',
 CC:'Commercial conversions starter guide',
 DEV:'Development starter guide',development_stage_open:'Development starter guide',
 DS:'Deal sourcing starter guide',
 NMD:'Assisted sales starter guide',
 capital:'Lending and co-investing starter guide',
 role_unresolved:'Choosing your investor role starter guide'
};

// The 34 versions Adam approved (28 Oct 2026: "Yeah, all the guides are good."), served by the hub from public/guides
// (scripts/build.mjs copies public into dist) at /guides/<file>.pdf: the guide's name in lower case with hyphens, then
// its version's short name ("video", never "masterclass", as on the files he approved), or nothing for the 2 investor
// guides, whose only version has no offer in its box. worksheets.test.mjs checks every file is there and every file is used.
const VERSION_FILE={'property-school':'property-school',masterclass:'video',scorecard:'scorecard',none:''};
const GUIDE_STEPS={
 'BRRR starter guide':['property-school','masterclass','scorecard'],
 'Buy-to-let starter guide':['property-school','masterclass','scorecard'],
 'HMO starter guide':['property-school','masterclass','scorecard'],
 'Serviced accommodation starter guide':['property-school','masterclass','scorecard'],
 'Buy-to-let with refurbishment starter guide':['property-school','masterclass','scorecard'],
 'Flips starter guide':['property-school','masterclass','scorecard'],
 'Commercial conversions starter guide':['property-school','masterclass','scorecard'],
 'Development starter guide':['property-school','masterclass','scorecard'],
 'Deal sourcing starter guide':['property-school','scorecard'],
 'Rent-to-rent starter guide':['masterclass','scorecard'],
 'Lease option starter guide':['masterclass','scorecard'],
 'Assisted sales starter guide':['masterclass','scorecard'],
 'Lending and co-investing starter guide':['none'],
 'Choosing your investor role starter guide':['none']
};
export const GUIDE_LABEL='Open your starter guide';
export const guideHref=(guide,step)=>'/guides/'+guide.toLowerCase().replace(/[^a-z0-9]+/g,'-')+(VERSION_FILE[step]?'-'+VERSION_FILE[step]:'')+'.pdf';
export const WORKSHEETS=Object.fromEntries(Object.entries(GUIDE_STEPS).map(([guide,steps])=>[guide,Object.fromEntries(steps.map(step=>[step,{label:GUIDE_LABEL,href:guideHref(guide,step)}]))]));

/** The guide button for a result and its answers, { label, href }, or null. map is for the tests; the app uses WORKSHEETS. */
export function worksheetFor(result,answers,map=WORKSHEETS){
 if(!Array.isArray(result?.primary)||!map)return null;
 const exercise=reportExercise(result),guide=Object.hasOwn(GUIDES,exercise)?GUIDES[exercise]:null;
 if(!guide||!Object.hasOwn(map,guide))return null;
 const versions=map[guide],step=nextStep(leadLabels(result,answers));
 const entry=versions&&typeof versions==='object'&&Object.hasOwn(versions,step)?versions[step]:null;
 return entry&&typeof entry.label==='string'&&typeof entry.href==='string'?{label:entry.label,href:entry.href}:null;
}

/** The guide for a saved report's row (Free resources' line under the report's), { name, href }, or null: the same
 * version the report's button opens, worked out again from the row as the saved report page does. */
export function savedGuide(saved){
 try{
  const s=saved&&upgradeSession({version:saved.version,answers:saved.answers,history:saved.history,contexts:saved.contexts||{},revision:0,createdAt:Date.now()});
  if(!s)return null;
  const r=evaluate(s.answers),w=worksheetFor(r,s.answers);
  return w?{name:GUIDES[reportExercise(r)],href:w.href}:null;
 }catch{return null;}
}
